import { useState, type FormEvent } from 'react';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { TextInput } from '../../../shared/components/TextInput';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { useCustodians, useLocations, useProjects } from '../hooks';
import {
  useCreateStockItem,
  useCreateStockMovement,
  useDownloadStockMovementInvoice,
  useReturnStockIssue,
  useStockIssues,
  useStockItems,
  useStockMovements,
  useStockPurchaseOrders,
  useUploadStockMovementInvoice,
  useUpdateStockItem,
  useUpdateStockLocationDetails,
} from '../hooks';
import {
  useCloseStockInventory,
  useCountStockInventoryItem,
  useCreateStockInventory,
  useStartStockInventory,
  useStockInventories,
  useStockInventory,
} from '../hooks';
import type { StockItem, StockMovementType } from '../api';
import type { StockInventoryStatus } from '../api';
import type { StockIssueRecipientType } from '../api';
import { useAreas } from '../../cadastros-organizacionais/hooks';

const MOVEMENT_LABELS: Record<StockMovementType, string> = {
  entry: 'Entrada',
  donation: 'Doação',
  exit: 'Saída',
  transfer: 'Transferência',
  adjustment: 'Ajuste de inventário',
  return: 'Devolução',
};

const selectClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';
const INVENTORY_STATUS_LABELS: Record<StockInventoryStatus, string> = {
  planned: 'Planejado',
  in_progress: 'Em andamento',
  closed: 'Encerrado',
};

type LocationDetailsForm = {
  unitName: string;
  building: string;
  floor: string;
  room: string;
  aisle: string;
  shelf: string;
  position: string;
};

const EMPTY_LOCATION_DETAILS: LocationDetailsForm = {
  unitName: '', building: '', floor: '', room: '', aisle: '', shelf: '', position: '',
};

const LOCATION_DETAIL_FIELDS: Array<{ key: keyof LocationDetailsForm; label: string }> = [
  { key: 'unitName', label: 'Unidade' },
  { key: 'building', label: 'Prédio' },
  { key: 'floor', label: 'Andar' },
  { key: 'room', label: 'Sala' },
  { key: 'aisle', label: 'Corredor' },
  { key: 'shelf', label: 'Prateleira' },
  { key: 'position', label: 'Posição' },
];

function locationLabel(location: { name: string; fullAddress?: string | null }) {
  return location.fullAddress ? `${location.name} — ${location.fullAddress}` : location.name;
}

export default function AlmoxarifadoPage() {
  const { can } = usePermissions();
  const canCreate = can(FEATURES.ativos, 'create');
  const canMove = can(FEATURES.ativos, 'edit');
  const [showInactiveItems, setShowInactiveItems] = useState(false);
  const itemsQuery = useStockItems({ active: !showInactiveItems, limit: 200 });
  const purchaseOrdersQuery = useStockPurchaseOrders();
  const locationsQuery = useLocations();
  const custodiansQuery = useCustodians({ active: true, limit: 100 });
  const projectsQuery = useProjects();
  const areasQuery = useAreas({ active: 'true', limit: 200 });
  const createItem = useCreateStockItem();
  const updateItem = useUpdateStockItem();
  const updateLocationDetails = useUpdateStockLocationDetails();
  const [selectedItemId, setSelectedItemId] = useState('');
  const createMovement = useCreateStockMovement(selectedItemId);
  const uploadMovementInvoice = useUploadStockMovementInvoice(selectedItemId);
  const downloadMovementInvoice = useDownloadStockMovementInvoice(selectedItemId);
  const movementsQuery = useStockMovements(selectedItemId || undefined);
  const issuesQuery = useStockIssues({ outstandingOnly: true, limit: 100 });
  const returnIssue = useReturnStockIssue();
  const inventoriesQuery = useStockInventories({ limit: 100 });
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const inventoryQuery = useStockInventory(selectedInventoryId || undefined);
  const createInventory = useCreateStockInventory();
  const startInventory = useStartStockInventory();
  const countInventoryItem = useCountStockInventoryItem(selectedInventoryId);
  const closeInventory = useCloseStockInventory();
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [showInventoryForm, setShowInventoryForm] = useState(false);
  const [message, setMessage] = useState<{ text: string; variant: 'success' | 'error' } | null>(null);
  const [movementType, setMovementType] = useState<StockMovementType>('entry');
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [itemForm, setItemForm] = useState({
    code: '', description: '', category: '', unit: 'un', minimumStock: '0', notes: '',
  });
  const [movementForm, setMovementForm] = useState({
    quantity: '', fromLocationId: '', toLocationId: '', reason: '', reference: '',
    purchaseOrderId: '', purchaseQuotationItemId: '', supplierInvoiceNumber: '', supplierInvoiceDate: '', unitCost: '', totalCost: '',
    recipientType: 'custodian' as StockIssueRecipientType,
    recipientId: '',
  });
  const [returnValues, setReturnValues] = useState<Record<string, { quantity: string; reason: string }>>({});
  const [inventoryName, setInventoryName] = useState('');
  const [inventoryLocationId, setInventoryLocationId] = useState('');
  const [locationDetailsId, setLocationDetailsId] = useState('');
  const [locationDetailsForm, setLocationDetailsForm] = useState<LocationDetailsForm>(EMPTY_LOCATION_DETAILS);
  const [countValues, setCountValues] = useState<Record<string, string>>({});

  async function handleCreateItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    try {
      const payload = {
        ...itemForm,
        code: itemForm.code.trim(),
        description: itemForm.description.trim(),
        category: itemForm.category.trim() || undefined,
        unit: itemForm.unit.trim(),
        minimumStock: Number(itemForm.minimumStock),
        notes: itemForm.notes.trim() || undefined,
      };
      const item = editingItemId
        ? await updateItem.mutateAsync({ id: editingItemId, payload })
        : await createItem.mutateAsync(payload);
      setItemForm({ code: '', description: '', category: '', unit: 'un', minimumStock: '0', notes: '' });
      setShowItemForm(false);
      setEditingItemId(null);
      setSelectedItemId(item.id);
      setMessage({
        text: editingItemId ? 'Item de estoque atualizado.' : 'Item de estoque cadastrado.',
        variant: 'success',
      });
    } catch {
      setMessage({
        text: editingItemId
          ? 'Não foi possível atualizar o item. Verifique os dados e se o código já está em uso.'
          : 'Não foi possível cadastrar o item. Verifique se o código já está em uso.',
        variant: 'error',
      });
    }
  }

  function editStockItem(item: StockItem) {
    setItemForm({
      code: item.code,
      description: item.description,
      category: item.category ?? '',
      unit: item.unit,
      minimumStock: item.minimumStock,
      notes: item.notes ?? '',
    });
    setEditingItemId(item.id);
    setShowItemForm(true);
    setMessage(null);
  }

  async function setStockItemActive(itemId: string, active: boolean, description: string) {
    if (!active && !window.confirm(`Inativar "${description}"? O saldo e o histórico serão preservados, mas novas movimentações serão bloqueadas.`)) {
      return;
    }
    setMessage(null);
    try {
      await updateItem.mutateAsync({ id: itemId, payload: { active } });
      if (selectedItemId === itemId) setSelectedItemId('');
      setMessage({
        text: active ? 'Item de estoque reativado.' : 'Item de estoque inativado; saldo e histórico foram preservados.',
        variant: 'success',
      });
    } catch {
      setMessage({
        text: active ? 'Não foi possível reativar o item.' : 'Não foi possível inativar o item.',
        variant: 'error',
      });
    }
  }

  async function handleMovement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedItemId) return;
    setMessage(null);
    if (movementType === 'entry' && movementForm.purchaseOrderId && !invoiceFile) {
      setMessage({ text: 'Anexe a nota fiscal para registrar o recebimento do pedido de compra.', variant: 'error' });
      return;
    }
    try {
      await createMovement.mutateAsync({
        payload: {
          type: movementType,
          quantity: Number(movementForm.quantity),
          fromLocationId: movementType === 'entry' || movementType === 'donation' ? undefined : movementForm.fromLocationId,
          toLocationId: movementType === 'exit' ? undefined : movementForm.toLocationId,
          reason: movementForm.reason.trim() || undefined,
          reference: movementForm.reference.trim() || undefined,
          recipientType: movementType === 'exit' ? movementForm.recipientType : undefined,
          custodianId: movementType === 'exit' && movementForm.recipientType === 'custodian' ? movementForm.recipientId : undefined,
          orgUnitId: movementType === 'exit' && movementForm.recipientType === 'org_unit' ? movementForm.recipientId : undefined,
          projectId: movementType === 'exit' && movementForm.recipientType === 'project' ? movementForm.recipientId : undefined,
          purchaseOrderId: movementType === 'entry' ? movementForm.purchaseOrderId || undefined : undefined,
          purchaseQuotationItemId: movementType === 'entry' && movementForm.purchaseOrderId
            ? movementForm.purchaseQuotationItemId || undefined : undefined,
          supplierInvoiceNumber: movementType === 'entry' && movementForm.purchaseOrderId
            ? movementForm.supplierInvoiceNumber.trim() : undefined,
          supplierInvoiceDate: movementType === 'entry' && movementForm.purchaseOrderId
            ? movementForm.supplierInvoiceDate : undefined,
          unitCost: movementType === 'entry' && movementForm.purchaseOrderId
            ? Number(movementForm.unitCost) : undefined,
          totalCost: movementType === 'entry' && movementForm.purchaseOrderId
            ? Number(movementForm.totalCost) : undefined,
        },
        invoiceFile: movementType === 'entry' && movementForm.purchaseOrderId ? invoiceFile ?? undefined : undefined,
      });
      setInvoiceFile(null);
      setMovementForm({
        quantity: '', fromLocationId: '', toLocationId: '', reason: '', reference: '',
        purchaseOrderId: '', purchaseQuotationItemId: '', supplierInvoiceNumber: '', supplierInvoiceDate: '', unitCost: '', totalCost: '',
        recipientType: 'custodian', recipientId: '',
      });
      setMessage({ text: `${MOVEMENT_LABELS[movementType]} registrada com sucesso.`, variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível registrar a movimentação. Confira os dados, o saldo, as localizações e o anexo fiscal.', variant: 'error' });
    }
  }

  async function handleReturn(event: FormEvent<HTMLFormElement>, issueId: string) {
    event.preventDefault();
    const values = returnValues[issueId];
    if (!values?.quantity) return;
    setMessage(null);
    try {
      await returnIssue.mutateAsync({
        id: issueId,
        payload: {
          quantity: Number(values.quantity),
          reason: values.reason.trim() || undefined,
        },
      });
      setReturnValues({ ...returnValues, [issueId]: { quantity: '', reason: '' } });
      setMessage({ text: 'Devolução registrada e saldo atualizado.', variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível registrar a devolução. Confira a quantidade ainda não retornada.', variant: 'error' });
    }
  }

  async function handleCreateInventory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    try {
      const inventory = await createInventory.mutateAsync({
        name: inventoryName.trim(),
        locationId: inventoryLocationId,
      });
      setInventoryName('');
      setInventoryLocationId('');
      setShowInventoryForm(false);
      setSelectedInventoryId(inventory.id);
      setMessage({ text: 'Campanha criada. Inicie para congelar os saldos esperados e começar as contagens.', variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível criar a campanha de inventário.', variant: 'error' });
    }
  }

  async function handleCount(event: FormEvent<HTMLFormElement>, itemId: string) {
    event.preventDefault();
    if (!selectedInventoryId) return;
    const existingCount = inventory?.items.find((item) => item.id === itemId)?.countedQuantity;
    const input = countValues[itemId] ?? (existingCount === null || existingCount === undefined
      ? ''
      : String(existingCount));
    if (input === undefined || input === '') return;
    const form = event.currentTarget;
    const formData = new FormData(form);
    setMessage(null);
    try {
      await countInventoryItem.mutateAsync({
        itemId,
        payload: {
          countedQuantity: Number(input),
          notes: String(formData.get('notes') ?? '').trim() || undefined,
        },
      });
      setMessage({ text: 'Contagem registrada.', variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível registrar a contagem deste material.', variant: 'error' });
    }
  }

  async function handleStartInventory() {
    if (!selectedInventoryId) return;
    setMessage(null);
    try {
      await startInventory.mutateAsync(selectedInventoryId);
      setCountValues({});
      setMessage({ text: 'Inventário iniciado. As movimentações desta localização ficam bloqueadas até o encerramento.', variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível iniciar o inventário. Verifique se já existe uma campanha ativa para o local.', variant: 'error' });
    }
  }

  async function handleCloseInventory() {
    if (!selectedInventoryId) return;
    setMessage(null);
    try {
      const result = await closeInventory.mutateAsync(selectedInventoryId);
      setMessage({
        text: `Inventário encerrado. ${result.adjustmentsApplied ?? 0} ajuste(s) de saldo registrado(s) no histórico.`,
        variant: 'success',
      });
    } catch {
      setMessage({ text: 'Não foi possível encerrar. Confira se todos os materiais foram contados.', variant: 'error' });
    }
  }

  async function handleSaveLocationDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!locationDetailsId) return;
    setMessage(null);
    try {
      await updateLocationDetails.mutateAsync({
        locationId: locationDetailsId,
        payload: Object.fromEntries(
          Object.entries(locationDetailsForm).map(([key, value]) => [key, value.trim() || null]),
        ),
      });
      setMessage({ text: 'Detalhamento físico do local atualizado.', variant: 'success' });
    } catch {
      setMessage({ text: 'Não foi possível atualizar o detalhamento físico do local.', variant: 'error' });
    }
  }

  const locations = locationsQuery.data?.data ?? [];
  const selectedLocation = locations.find((location) => location.id === locationDetailsId);
  const selectedItem = itemsQuery.data?.data.find((item) => item.id === selectedItemId);
  const selectedPurchaseLine = purchaseOrdersQuery.data?.data
    .find((order) => order.id === movementForm.purchaseOrderId)?.lines
    .find((line) => line.id === movementForm.purchaseQuotationItemId);
  const inventory = inventoryQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Controle de materiais de consumo, com saldo independente por localização.
        </p>
        {canCreate && (
          <Button onClick={() => {
            if (showItemForm) {
              setShowItemForm(false);
              setEditingItemId(null);
              setItemForm({ code: '', description: '', category: '', unit: 'un', minimumStock: '0', notes: '' });
            } else {
              setEditingItemId(null);
              setShowItemForm(true);
            }
          }}>
            {showItemForm
              ? editingItemId ? 'Cancelar edição' : 'Cancelar cadastro'
              : 'Novo item'}
          </Button>
        )}
      </div>

      {message && <Alert variant={message.variant}>{message.text}</Alert>}
      {(itemsQuery.isError || locationsQuery.isError) && (
        <Alert variant="error">Não foi possível carregar os dados do almoxarifado.</Alert>
      )}

      {canMove && (
        <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <h2 className="font-semibold text-gray-800">Endereçamento físico dos locais</h2>
            <p className="mt-1 text-sm text-gray-500">Detalhe unidade, prédio e posição para localizar materiais no almoxarifado.</p>
          </div>
          <form onSubmit={handleSaveLocationDetails} className="grid grid-cols-1 items-end gap-4 border-t border-gray-100 pt-4 md:grid-cols-3">
            <label className="block space-y-1 text-sm text-gray-700">
              <span>Local *</span>
              <select
                className={selectClass}
                value={locationDetailsId}
                onChange={(event) => {
                  const id = event.target.value;
                  const location = locations.find((entry) => entry.id === id);
                  setLocationDetailsId(id);
                  setLocationDetailsForm(location ? {
                    unitName: location.unitName ?? '',
                    building: location.building ?? '',
                    floor: location.floor ?? '',
                    room: location.room ?? '',
                    aisle: location.aisle ?? '',
                    shelf: location.shelf ?? '',
                    position: location.position ?? '',
                  } : EMPTY_LOCATION_DETAILS);
                }}
                required
              >
                <option value="">Selecione</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
              </select>
            </label>
            {LOCATION_DETAIL_FIELDS.map(({ key, label }) => (
              <TextInput
                key={key}
                label={label}
                value={locationDetailsForm[key]}
                onChange={(event) => setLocationDetailsForm({ ...locationDetailsForm, [key]: event.target.value })}
                maxLength={key === 'floor' ? 80 : 120}
              />
            ))}
            <Button type="submit" loading={updateLocationDetails.isPending} disabled={!selectedLocation}>
              Salvar endereçamento
            </Button>
          </form>
        </section>
      )}

      <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-800">Inventário quantitativo</h2>
            <p className="mt-1 text-sm text-gray-500">Conferência física por localização, com ajuste auditado dos saldos.</p>
          </div>
          {canCreate && (
            <Button variant="secondary" onClick={() => setShowInventoryForm((visible) => !visible)}>
              {showInventoryForm ? 'Cancelar' : 'Nova campanha'}
            </Button>
          )}
        </div>

        {showInventoryForm && (
          <form onSubmit={handleCreateInventory} className="grid grid-cols-1 items-end gap-4 border-t border-gray-100 pt-4 md:grid-cols-3">
            <TextInput label="Nome da campanha *" value={inventoryName} onChange={(e) => setInventoryName(e.target.value)} required minLength={2} maxLength={255} />
            <label className="block space-y-1 text-sm text-gray-700">
              <span>Localização *</span>
              <select className={selectClass} value={inventoryLocationId} onChange={(e) => setInventoryLocationId(e.target.value)} required>
                <option value="">Selecione</option>
                {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
              </select>
            </label>
            <Button type="submit" loading={createInventory.isPending}>Criar campanha</Button>
          </form>
        )}

        {inventoriesQuery.isError && <Alert variant="error">Não foi possível carregar as campanhas de inventário.</Alert>}
        {(inventoriesQuery.data?.data.length ?? 0) > 0 && (
          <div className="overflow-x-auto border-t border-gray-100 pt-3">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="py-2 pr-4">Campanha</th><th className="py-2 pr-4">Local</th><th className="py-2 pr-4">Situação</th><th className="py-2 pr-4">Contagem / divergências</th><th className="py-2">Ação</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inventoriesQuery.data?.data.map((campaign) => (
                  <tr key={campaign.id} className={selectedInventoryId === campaign.id ? 'bg-blue-50' : ''}>
                    <td className="py-2 pr-4 font-medium text-gray-800">{campaign.name}</td>
                    <td className="py-2 pr-4">{campaign.location?.name ?? '—'}</td>
                    <td className="py-2 pr-4">{INVENTORY_STATUS_LABELS[campaign.status]}</td>
                    <td className="py-2 pr-4">{campaign.progress.counted}/{campaign.progress.total} · {campaign.progress.divergent} divergência(s)</td>
                    <td className="py-2">
                      <Button size="sm" variant="secondary" onClick={() => setSelectedInventoryId(campaign.id)}>
                        {selectedInventoryId === campaign.id ? 'Selecionada' : 'Abrir'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {inventory && (
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-medium text-gray-800">{inventory.name} · {inventory.location?.name ?? 'Local'}</h3>
                <p className="text-sm text-gray-500">
                  {INVENTORY_STATUS_LABELS[inventory.status]} · {inventory.progress.counted}/{inventory.progress.total} contados · {inventory.progress.divergent} divergência(s)
                </p>
              </div>
              {inventory.status === 'planned' && canMove && (
                <Button onClick={handleStartInventory} loading={startInventory.isPending}>Iniciar contagem</Button>
              )}
              {inventory.status === 'in_progress' && canMove && (
                <Button
                  onClick={handleCloseInventory}
                  loading={closeInventory.isPending}
                  disabled={inventory.progress.pending > 0}
                >
                  Aplicar ajustes e encerrar
                </Button>
              )}
            </div>

            {inventory.status === 'in_progress' && inventory.progress.pending > 0 && (
              <Alert variant="warning">Conte todos os materiais, inclusive os que estiverem com quantidade zero, antes de encerrar.</Alert>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr><th className="px-3 py-2">Material</th><th className="px-3 py-2">Esperado</th><th className="px-3 py-2">Contado</th><th className="px-3 py-2">Diferença</th><th className="px-3 py-2">Observação / ação</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {inventory.items.map((line) => (
                    <tr key={line.id} className={line.difference !== null && line.difference !== 0 ? 'bg-amber-50/50' : ''}>
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs text-gray-500">{line.item?.code}</span>
                        <div className="font-medium text-gray-800">{line.item?.description}</div>
                      </td>
                      <td className="px-3 py-2">{line.expectedQuantity.toLocaleString('pt-BR')} {line.item?.unit}</td>
                      <td className="px-3 py-2">
                        {inventory.status === 'in_progress'
                          ? <input
                              aria-label={`Quantidade contada de ${line.item?.description}`}
                              className="w-28 rounded border border-gray-300 px-2 py-1"
                              type="number"
                              min="0"
                              step="0.001"
                              value={countValues[line.id] ?? (line.countedQuantity === null ? '' : String(line.countedQuantity))}
                              onChange={(e) => setCountValues({ ...countValues, [line.id]: e.target.value })}
                            />
                          : line.countedQuantity === null ? '—' : `${line.countedQuantity.toLocaleString('pt-BR')} ${line.item?.unit}`}
                        {inventory.status === 'in_progress' && <span className="ml-1 text-gray-500">{line.item?.unit}</span>}
                      </td>
                      <td className="px-3 py-2">
                        {line.difference === null ? '—' : (
                          <span className={line.difference === 0 ? 'text-emerald-700' : 'font-semibold text-amber-700'}>
                            {line.difference > 0 ? '+' : ''}{line.difference.toLocaleString('pt-BR')} {line.item?.unit}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {inventory.status === 'in_progress' ? (
                          <form className="flex min-w-[300px] items-center gap-2" onSubmit={(event) => void handleCount(event, line.id)}>
                            <input
                              aria-label={`Observação da contagem de ${line.item?.description}`}
                              className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1"
                              name="notes"
                              placeholder="Observação opcional"
                              defaultValue={line.notes ?? ''}
                              maxLength={2000}
                            />
                            <Button
                              type="submit"
                              size="sm"
                              variant="secondary"
                              loading={countInventoryItem.isPending}
                              disabled={
                                countValues[line.id] === '' ||
                                (countValues[line.id] === undefined && line.countedQuantity === null)
                              }
                            >
                              {line.countedQuantity === null ? 'Registrar' : 'Atualizar'}
                            </Button>
                          </form>
                        ) : line.notes || '—'}
                      </td>
                    </tr>
                  ))}
                  {inventory.items.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">Não há materiais ativos para contar nesta campanha.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {showItemForm && (
        <form onSubmit={handleCreateItem} className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-4 font-semibold text-gray-800">
            {editingItemId ? 'Editar material de consumo' : 'Cadastrar material de consumo'}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <TextInput label="Código interno *" value={itemForm.code} onChange={(e) => setItemForm({ ...itemForm, code: e.target.value })} required maxLength={80} />
            <TextInput label="Descrição *" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} required maxLength={500} />
            <TextInput label="Categoria" value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} maxLength={120} />
            <TextInput label="Unidade de medida *" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} required maxLength={30} />
            <TextInput label="Estoque mínimo" type="number" min="0" step="0.001" value={itemForm.minimumStock} onChange={(e) => setItemForm({ ...itemForm, minimumStock: e.target.value })} />
            <TextInput label="Observações" value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} maxLength={2000} />
          </div>
          <div className="mt-4 flex justify-end">
            {editingItemId && (
              <Button
                type="button"
                variant="secondary"
                className="mr-2"
                onClick={() => {
                  setShowItemForm(false);
                  setEditingItemId(null);
                  setItemForm({ code: '', description: '', category: '', unit: 'un', minimumStock: '0', notes: '' });
                }}
              >
                Cancelar
              </Button>
            )}
            <Button
              type="submit"
              loading={createItem.isPending || updateItem.isPending}
            >
              {editingItemId ? 'Salvar alterações' : 'Salvar item'}
            </Button>
          </div>
        </form>
      )}

      <div className="flex justify-end">
        <Button
          variant="secondary"
          onClick={() => {
            setShowInactiveItems((show) => !show);
            setSelectedItemId('');
          }}
        >
          {showInactiveItems ? 'Ver itens ativos' : 'Ver itens inativos'}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Código / Material</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3">Saldo por localização</th>
              <th className="px-4 py-3">Total / mínimo</th>
              <th className="px-4 py-3">Situação</th>
              <th className="px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {itemsQuery.isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Carregando materiais…</td></tr>}
            {!itemsQuery.isLoading && (itemsQuery.data?.data.length ?? 0) === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                {showInactiveItems ? 'Nenhum material inativo.' : 'Nenhum material cadastrado.'}
              </td></tr>
            )}
            {itemsQuery.data?.data.map((item) => (
              <tr key={item.id} className={item.lowStock ? 'bg-amber-50/50' : ''}>
                <td className="px-4 py-3">
                  <div className="font-mono text-xs text-gray-500">{item.code}</div>
                  <div className="font-medium text-gray-900">{item.description}</div>
                </td>
                <td className="px-4 py-3 text-gray-600">{item.category || '—'}</td>
                <td className="px-4 py-3 text-gray-600">
                  {item.balances.length === 0 ? 'Sem saldo' : item.balances.map((balance) => (
                    <div key={balance.locationId}>{balance.locationName}: {Number(balance.quantity).toLocaleString('pt-BR')} {item.unit}</div>
                  ))}
                </td>
                <td className="px-4 py-3">
                  <span className={item.lowStock ? 'font-semibold text-amber-700' : 'text-gray-900'}>
                    {item.totalQuantity.toLocaleString('pt-BR')} {item.unit}
                  </span>
                  <div className="text-xs text-gray-500">Mínimo: {Number(item.minimumStock).toLocaleString('pt-BR')}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={item.active ? 'text-emerald-700' : 'text-gray-500'}>
                    {item.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {canMove && (
                      <Button size="sm" variant="secondary" onClick={() => editStockItem(item)}>
                        Editar
                      </Button>
                    )}
                    {canMove && item.active && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={updateItem.isPending}
                        onClick={() => void setStockItemActive(item.id, false, item.description)}
                      >
                        Inativar
                      </Button>
                    )}
                    {canMove && !item.active && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={updateItem.isPending}
                        onClick={() => void setStockItemActive(item.id, true, item.description)}
                      >
                        Reativar
                      </Button>
                    )}
                    {canMove && item.active && (
                      <Button size="sm" variant="secondary" onClick={() => {
                        setSelectedItemId(item.id);
                        setMessage(null);
                      }}>
                        Movimentar
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedItem && canMove && (
        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-4 font-semibold text-gray-800">
            Movimentar: {selectedItem.description} <span className="font-normal text-gray-500">({selectedItem.code})</span>
          </h2>
          <form onSubmit={handleMovement} className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 lg:grid-cols-3">
            <label className="block space-y-1 text-sm text-gray-700">
              <span>Tipo de movimentação</span>
              <select
                className={selectClass}
                value={movementType}
                onChange={(e) => {
                  setMovementType(e.target.value as StockMovementType);
                  setInvoiceFile(null);
                }}
              >
                <option value="entry">Entrada</option>
                <option value="donation">Doação</option>
                <option value="exit">Saída</option>
                <option value="transfer">Transferência</option>
              </select>
            </label>
            <TextInput
              label={`Quantidade (${selectedItem.unit}) *`}
              type="number"
              min="0.001"
              max={movementType === 'entry' && movementForm.purchaseOrderId ? selectedPurchaseLine?.remainingQuantity : undefined}
              step="0.001"
              value={movementForm.quantity}
              onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })}
              required
            />
            {movementType !== 'entry' && movementType !== 'donation' && (
              <label className="block space-y-1 text-sm text-gray-700">
                <span>Local de origem *</span>
                <select className={selectClass} value={movementForm.fromLocationId} onChange={(e) => setMovementForm({ ...movementForm, fromLocationId: e.target.value })} required>
                  <option value="">Selecione</option>
                  {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
                </select>
              </label>
            )}
            {movementType !== 'exit' && (
              <label className="block space-y-1 text-sm text-gray-700">
                <span>Local de destino *</span>
                <select className={selectClass} value={movementForm.toLocationId} onChange={(e) => setMovementForm({ ...movementForm, toLocationId: e.target.value })} required>
                  <option value="">Selecione</option>
                  {locations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
                </select>
              </label>
            )}
            {movementType === 'entry' && (
              <>
                <label className="block space-y-1 text-sm text-gray-700">
                  <span>Pedido de compra (opcional)</span>
                  <select
                    className={selectClass}
                    value={movementForm.purchaseOrderId}
                    onChange={(e) => {
                      setMovementForm({
                        ...movementForm,
                        purchaseOrderId: e.target.value,
                        purchaseQuotationItemId: '',
                      });
                      setInvoiceFile(null);
                    }}
                  >
                    <option value="">Entrada avulsa ou outra origem</option>
                    {purchaseOrdersQuery.data?.data.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.number} · {order.supplier} · {Number(order.amount).toLocaleString('pt-BR', {
                          style: 'currency', currency: 'BRL',
                        })}
                      </option>
                    ))}
                  </select>
                </label>
                {movementForm.purchaseOrderId && (
                  <>
                    <label className="block space-y-1 text-sm text-gray-700">
                      <span>Linha do pedido *</span>
                      <select
                        className={selectClass}
                        value={movementForm.purchaseQuotationItemId}
                        onChange={(e) => setMovementForm({ ...movementForm, purchaseQuotationItemId: e.target.value })}
                        required
                      >
                        <option value="">Selecione uma linha pendente</option>
                        {purchaseOrdersQuery.data?.data
                          .find((order) => order.id === movementForm.purchaseOrderId)?.lines.map((line) => (
                            <option
                              key={line.id}
                              value={line.id}
                              disabled={line.remainingQuantity <= 0}
                            >
                              {line.description} · Restam {line.remainingQuantity} {line.unit}
                            </option>
                          ))}
                      </select>
                    </label>
                    <TextInput
                      label="Número da nota fiscal *"
                      value={movementForm.supplierInvoiceNumber}
                      onChange={(e) => setMovementForm({ ...movementForm, supplierInvoiceNumber: e.target.value })}
                      maxLength={80}
                      required
                    />
                    <TextInput
                      label="Data da nota fiscal *"
                      type="date"
                      value={movementForm.supplierInvoiceDate}
                      onChange={(e) => setMovementForm({ ...movementForm, supplierInvoiceDate: e.target.value })}
                      required
                    />
                    <TextInput
                      label="Valor unitário (R$) *"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={movementForm.unitCost}
                      onChange={(e) => setMovementForm({ ...movementForm, unitCost: e.target.value })}
                      required
                    />
                    <TextInput
                      label="Valor total (R$) *"
                      type="number"
                      min="0"
                      step="0.01"
                      value={movementForm.totalCost}
                      onChange={(e) => setMovementForm({ ...movementForm, totalCost: e.target.value })}
                      required
                    />
                    <label className="block space-y-1 text-sm text-gray-700">
                      <span>Anexo da nota fiscal * (PDF, XML ou imagem)</span>
                      <input
                        type="file"
                        accept=".pdf,.xml,.png,.jpg,.jpeg"
                        required
                        onChange={(event) => setInvoiceFile(event.target.files?.[0] ?? null)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                  </>
                )}
              </>
            )}
            {movementType === 'exit' && (
              <>
                <label className="block space-y-1 text-sm text-gray-700">
                  <span>Tipo de destinatário *</span>
                  <select
                    className={selectClass}
                    value={movementForm.recipientType}
                    onChange={(e) => setMovementForm({
                      ...movementForm,
                      recipientType: e.target.value as StockIssueRecipientType,
                      recipientId: '',
                    })}
                    required
                  >
                    <option value="custodian">Colaborador ou prestador</option>
                    <option value="org_unit">Departamento / área</option>
                    <option value="project">Projeto</option>
                  </select>
                </label>
                <label className="block space-y-1 text-sm text-gray-700">
                  <span>Destinatário *</span>
                  <select
                    className={selectClass}
                    value={movementForm.recipientId}
                    onChange={(e) => setMovementForm({ ...movementForm, recipientId: e.target.value })}
                    required
                  >
                    <option value="">Selecione</option>
                    {movementForm.recipientType === 'custodian' &&
                      custodiansQuery.data?.data.map((custodian) => (
                        <option key={custodian.id} value={custodian.id}>
                          {custodian.name}{custodian.jobTitle ? ` · ${custodian.jobTitle}` : ''}
                        </option>
                      ))}
                    {movementForm.recipientType === 'org_unit' &&
                      areasQuery.data?.data.map((area) => (
                        <option key={area.id} value={area.id}>{area.code} · {area.name}</option>
                      ))}
                    {movementForm.recipientType === 'project' &&
                      projectsQuery.data?.data.map((project) => (
                        <option key={project.id} value={project.id}>{project.code} · {project.name}</option>
                      ))}
                  </select>
                </label>
              </>
            )}
            <TextInput label="Motivo (compra, doação, entrega…)" value={movementForm.reason} onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })} maxLength={500} />
            <TextInput label="Referência" value={movementForm.reference} onChange={(e) => setMovementForm({ ...movementForm, reference: e.target.value })} maxLength={120} />
            <div className="md:col-span-2 lg:col-span-3 flex justify-end">
              <Button type="submit" loading={createMovement.isPending}>Registrar movimentação</Button>
            </div>
          </form>
          {movementType === 'exit' && (custodiansQuery.isError || areasQuery.isError || projectsQuery.isError) && (
            <Alert variant="warning" className="mt-3">
              Não foi possível carregar todas as opções de destinatário. Confira suas permissões de consulta a responsáveis, áreas e projetos.
            </Alert>
          )}
          {movementType === 'entry' && purchaseOrdersQuery.isError && (
            <Alert variant="warning" className="mt-3">
              Não foi possível carregar pedidos de compra. Verifique sua conexão e tente novamente; entradas avulsas continuam disponíveis.
            </Alert>
          )}
          {movementsQuery.isError && <Alert variant="error">Não foi possível carregar o histórico do item.</Alert>}
          {(movementsQuery.data?.data.length ?? 0) > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Histórico recente</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500">
                    <tr><th className="py-2 pr-4">Data</th><th className="py-2 pr-4">Tipo</th><th className="py-2 pr-4">Quantidade</th><th className="py-2 pr-4">Origem → destino</th><th className="py-2">Motivo / referência</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {movementsQuery.data?.data.map((movement) => (
                      <tr key={movement.id}>
                        <td className="py-2 pr-4 whitespace-nowrap">{new Date(movement.occurredAt).toLocaleString('pt-BR')}</td>
                        <td className="py-2 pr-4">{MOVEMENT_LABELS[movement.type]}</td>
                        <td className="py-2 pr-4">{Number(movement.quantity).toLocaleString('pt-BR')} {selectedItem.unit}</td>
                        <td className="py-2 pr-4">
                          {movement.fromLocation ? locationLabel(movement.fromLocation) : '—'}
                          {' → '}
                          {movement.toLocation ? locationLabel(movement.toLocation) : '—'}
                        </td>
                        <td className="py-2">
                          {[movement.reason, movement.reference].filter(Boolean).join(' · ') || '—'}
                          {movement.purchaseOrder && (
                            <div className="mt-1 text-xs text-gray-500">
                              Pedido {movement.purchaseOrder.number} · {movement.supplier?.name ?? 'Fornecedor'}
                              {movement.purchaseOrderLine && ` · ${movement.purchaseOrderLine.description}`}
                              {movement.supplierInvoiceNumber && ` · NF ${movement.supplierInvoiceNumber}`}
                              {movement.supplierInvoiceDate && ` (${new Date(`${movement.supplierInvoiceDate}T00:00:00`).toLocaleDateString('pt-BR')})`}
                              {movement.unitCost !== null && movement.totalCost !== null && (
                                <div>
                                  {Number(movement.unitCost).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  {' / '}
                                  {Number(movement.totalCost).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                </div>
                              )}
                              {movement.invoiceDocument ? (
                                <button
                                  type="button"
                                  className="ml-2 text-blue-700 underline"
                                  disabled={downloadMovementInvoice.isPending}
                                  onClick={() => void downloadMovementInvoice.mutateAsync(movement.id).catch(() => {
                                    setMessage({ text: 'Não foi possível baixar a nota fiscal desta movimentação.', variant: 'error' });
                                  })}
                                >
                                  {movement.invoiceDocument.fileName}
                                </button>
                              ) : (
                                <label className="mt-1 flex items-center gap-2">
                                  <span>Nota fiscal não anexada:</span>
                                  <input
                                    type="file"
                                    accept=".pdf,.xml,.png,.jpg,.jpeg"
                                    disabled={uploadMovementInvoice.isPending}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      if (file) {
                                        void uploadMovementInvoice.mutateAsync({ movementId: movement.id, file })
                                          .then(() => setMessage({ text: 'Nota fiscal anexada.', variant: 'success' }))
                                          .catch(() => setMessage({ text: 'Não foi possível anexar a nota fiscal.', variant: 'error' }));
                                      }
                                    }}
                                  />
                                </label>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {canMove && (
        <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <h2 className="font-semibold text-gray-800">Saídas com devolução pendente</h2>
            <p className="mt-1 text-sm text-gray-500">Registre devoluções parciais ou totais; cada devolução repõe o saldo e entra no histórico.</p>
          </div>
          {issuesQuery.isError && <Alert variant="error">Não foi possível carregar as saídas pendentes de devolução.</Alert>}
          {(issuesQuery.data?.data.length ?? 0) === 0 && !issuesQuery.isLoading && (
            <p className="text-sm text-gray-500">Nenhuma saída está aguardando devolução.</p>
          )}
          {issuesQuery.isLoading && <p className="text-sm text-gray-500">Carregando saídas pendentes…</p>}
          {issuesQuery.data?.data.map((issue) => {
            const values = returnValues[issue.id] ?? { quantity: '', reason: '' };
            return (
              <form
                key={issue.id}
                onSubmit={(event) => void handleReturn(event, issue.id)}
                className="grid grid-cols-1 items-end gap-3 border-t border-gray-100 pt-3 md:grid-cols-2 lg:grid-cols-6"
              >
                <div className="lg:col-span-2">
                  <div className="font-medium text-gray-800">{issue.item?.description ?? 'Material'}</div>
                  <div className="text-xs text-gray-500">
                    {issue.item?.code} · {issue.recipient?.name ?? 'Destinatário'} · {issue.location?.name ?? 'Local'}
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  Pendente: <strong>{issue.outstandingQuantity.toLocaleString('pt-BR')} {issue.item?.unit}</strong>
                </div>
                <TextInput
                  label={`Quantidade devolvida (${issue.item?.unit}) *`}
                  type="number"
                  min="0.001"
                  max={issue.outstandingQuantity}
                  step="0.001"
                  value={values.quantity}
                  onChange={(e) => setReturnValues({
                    ...returnValues,
                    [issue.id]: { ...values, quantity: e.target.value },
                  })}
                  required
                />
                <TextInput
                  label="Motivo"
                  value={values.reason}
                  onChange={(e) => setReturnValues({
                    ...returnValues,
                    [issue.id]: { ...values, reason: e.target.value },
                  })}
                  maxLength={500}
                />
                <Button
                  type="submit"
                  size="sm"
                  loading={returnIssue.isPending}
                  disabled={!values.quantity || Number(values.quantity) > issue.outstandingQuantity}
                >
                  Registrar devolução
                </Button>
              </form>
            );
          })}
        </section>
      )}
    </div>
  );
}
